import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { API_BASE_URL } from "../../utils/constants";
import PageCenter, { NAVY } from "./PageCenter";
import {
  FaFilePdf, FaDownload, FaEye,
  FaTimes, FaFolderOpen, FaThLarge, FaList,
} from "react-icons/fa";

/* ══════════════════════════════════════════
   Design tokens — single source of truth
══════════════════════════════════════════ */
const T = {
  navy:       NAVY,
  gold:       "#eaa637",
  font:       "Poppins, sans-serif",
  border:     "#e8ecf0",
  pageBg:     "#f4f6f8",
  text:       "#1e293b",
  muted:      "#94a3b8",
  pdfBg:      "#fff7ed",
  pdfBorder:  "#fed7aa",
  pdfIcon:    "#ea580c",
  shadowSm:   "0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.06)",
  shadowCard: "0 1px 3px rgba(0,0,0,0.07), 0 4px 10px rgba(0,0,0,0.05)",
  shadowHover:"0 6px 16px rgba(0,0,0,0.13)",
};

const STATUS_CONFIG = {
  Active:     { bg: "#d1fae5", color: "#065f46", border: "#6ee7b7" },
  "On Hold":  { bg: "#fef3c7", color: "#92400e", border: "#fcd34d" },
  Terminated: { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5" },
  _default:   { bg: "#f3f4f6", color: "#374151", border: "#d1d5db" },
};

const LABEL_STYLE = {
  fontSize: "0.67rem", fontWeight: 600,
  textTransform: "uppercase", letterSpacing: "0.06em",
  color: T.muted, marginBottom: 3,
};

/* ══════════════════════════════════════════
   Injected CSS — hover / transition states
   (replaces all onMouseEnter/Leave hacks
   and useState-based hover in ActionBtn)
══════════════════════════════════════════ */
const STYLES = `
  * { box-sizing: border-box; }

  .cfm-btn {
    display: inline-flex; align-items: center; justify-content: center;
    gap: 4px; border-radius: 6px; font-weight: 600;
    font-family: ${T.font}; transition: all 0.15s;
    cursor: pointer; white-space: nowrap;
  }
  .cfm-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .cfm-btn-sm  { font-size: 0.7rem;  padding: 4px 10px; }
  .cfm-btn-md  { font-size: 0.75rem; padding: 5px 14px; }
  .cfm-btn-full { width: 100%; }

  .cfm-outline { border: 1px solid #cbd5e1; background: #fff; color: ${T.navy}; }
  .cfm-outline:not(:disabled):hover { background: ${T.navy}; color: #fff; border-color: ${T.navy}; }

  .cfm-solid { border: none; background: ${T.navy}; color: ${T.gold}; }
  .cfm-solid:not(:disabled):hover { opacity: 0.85; }

  .cfm-list-row {
    display: flex; align-items: center; gap: 0.9rem;
    padding: 0.75rem 1.25rem; transition: background 0.12s;
  }
  .cfm-list-row:hover { background: #f8fafc; }

  .cfm-grid-card {
    background: #fff; border-radius: 12px;
    border: 1px solid ${T.border}; box-shadow: ${T.shadowCard};
    overflow: hidden; display: flex; flex-direction: column;
    transition: box-shadow 0.18s, transform 0.18s;
  }
  .cfm-grid-card:hover { transform: translateY(-3px); box-shadow: ${T.shadowHover}; }

  .cfm-grid-preview {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    padding: 1.75rem 0 1.5rem; border-bottom: 1px solid #f1f5f9;
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; gap: 0.5rem; cursor: pointer;
  }
  .cfm-grid-preview:hover { background: linear-gradient(135deg, #f1f5f9 0%, #e8ecf0 100%); }

  .cfm-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.65);
    z-index: 9999; display: flex; flex-direction: column;
    font-family: ${T.font};
  }

  .cfm-close-btn {
    background: transparent; border: 1px solid rgba(234,166,55,0.45);
    color: ${T.gold}; border-radius: 6px; padding: 3px 14px;
    font-weight: 600; font-size: 0.78rem; cursor: pointer;
    display: inline-flex; align-items: center; gap: 5px;
    font-family: ${T.font}; transition: background 0.15s;
  }
  .cfm-close-btn:hover { background: rgba(234,166,55,0.1); }

  .cfm-toggle-btn {
    width: 30px; height: 30px; border-radius: 6px; border: none;
    cursor: pointer; display: flex; align-items: center;
    justify-content: center; transition: all 0.15s;
  }
`;

/* ══════════════════════════════════════════
   Shared utilities
══════════════════════════════════════════ */
async function fetchDocBlob(serviceId) {
  const res = await fetch(`${API_BASE_URL}/api/public-file?id=${serviceId}`);
  if (!res.ok) throw new Error("Request failed");
  return res.blob();
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a   = Object.assign(document.createElement("a"), { href: url, download: filename });
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ══════════════════════════════════════════
   Small shared components
══════════════════════════════════════════ */
function Spinner({ size = "0.65rem" }) {
  return (
    <span
      className="spinner-border spinner-border-sm"
      style={{ width: size, height: size, flexShrink: 0 }}
    />
  );
}

function ActionBtn({ variant = "outline", size = "md", fullWidth, icon, label, onClick, disabled }) {
  return (
    <button
      className={`cfm-btn cfm-btn-${size} cfm-${variant}${fullWidth ? " cfm-btn-full" : ""}`}
      onClick={onClick}
      disabled={disabled}
    >
      {icon}
      {label && <span>{label}</span>}
    </button>
  );
}

function PdfIconBox({ iconSize, boxW, boxH }) {
  return (
    <div style={{
      flexShrink: 0, width: boxW, height: boxH, borderRadius: 8,
      background: T.pdfBg, border: `1px solid ${T.pdfBorder}`,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <FaFilePdf size={iconSize} color={T.pdfIcon} />
    </div>
  );
}

/* ══════════════════════════════════════════
   Document row (list view)
══════════════════════════════════════════ */
function DocListItem({ doc, isLast, onPreview, onDownload, previewLoading, downloading }) {
  const isDownloading = downloading === doc.serviceId;
  return (
    <div
      className="cfm-list-row"
      style={{ borderBottom: isLast ? "none" : `1px solid #f1f5f9` }}
    >
      <PdfIconBox iconSize={17} boxW={36} boxH={36} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: "0.875rem", color: T.text }}>{doc.label}</div>
        <div style={{ fontSize: "0.68rem", color: T.muted, marginTop: 1 }}>PDF Document</div>
      </div>

      <div style={{ display: "flex", gap: "0.45rem" }}>
        <ActionBtn
          variant="outline"
          icon={<FaEye size={11} />}
          label="Preview"
          onClick={() => onPreview(doc)}
          disabled={previewLoading}
        />
        <ActionBtn
          variant="solid"
          icon={isDownloading ? <Spinner size="0.62rem" /> : <FaDownload size={10} />}
          label={isDownloading ? "" : "Download"}
          onClick={() => onDownload(doc)}
          disabled={isDownloading}
        />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   Document card (grid view)
══════════════════════════════════════════ */
function DocGridCard({ doc, onPreview, onDownload, downloading }) {
  const isDownloading = downloading === doc.serviceId;
  return (
    <div className="cfm-grid-card">
      <div className="cfm-grid-preview" onClick={() => onPreview(doc)}>
        <div style={{
          width: 52, height: 62, borderRadius: 6,
          background: T.pdfBg, border: `1px solid ${T.pdfBorder}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
        }}>
          <FaFilePdf size={28} color={T.pdfIcon} />
        </div>
        <span style={{ fontSize: "0.64rem", color: T.muted, fontWeight: 500 }}>Click to preview</span>
      </div>

      <div style={{ padding: "0.7rem 0.85rem 0.85rem", flex: 1, display: "flex", flexDirection: "column", gap: "0.6rem" }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: "0.8rem", color: T.text, lineHeight: 1.3 }}>{doc.label}</div>
          <div style={{ fontSize: "0.65rem", color: T.muted, marginTop: 2 }}>PDF Document</div>
        </div>
        <ActionBtn
          variant="solid"
          size="sm"
          fullWidth
          icon={isDownloading ? <Spinner size="0.55rem" /> : <FaDownload size={9} />}
          label={isDownloading ? "" : "Download"}
          onClick={() => onDownload(doc)}
          disabled={isDownloading}
        />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   Preview modal
══════════════════════════════════════════ */
function PreviewModal({ doc, loading, onClose }) {
  return (
    <div className="cfm-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: T.navy, padding: "0.6rem 1.25rem",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
      }}>
        <span style={{ color: T.gold, fontWeight: 700, fontSize: "0.88rem", display: "flex", alignItems: "center", gap: 7 }}>
          <FaFilePdf size={14} /> {doc.label}
        </span>
        <button className="cfm-close-btn" onClick={onClose}>
          <FaTimes size={10} /> Close
        </button>
      </div>

      <div style={{ flex: 1, overflow: "hidden" }}>
        {loading || !doc.url ? (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
            <div style={{ textAlign: "center" }}>
              <div className="spinner-border" style={{ color: T.gold }} />
              <p style={{ marginTop: "0.75rem", fontSize: "0.82rem" }}>Loading document…</p>
            </div>
          </div>
        ) : (
          <iframe src={doc.url} title={doc.label} style={{ width: "100%", height: "100%", border: "none" }} />
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   View toggle button group
══════════════════════════════════════════ */
const VIEW_MODES = [
  { mode: "list", Icon: FaList,    title: "List view" },
  { mode: "grid", Icon: FaThLarge, title: "Grid view" },
];

function ViewToggle({ active, onChange }) {
  return (
    <div style={{ display: "flex", background: T.border, borderRadius: 8, padding: 3, gap: 2 }}>
      {VIEW_MODES.map(({ mode, Icon, title }) => (
        <button
          key={mode}
          title={title}
          onClick={() => onChange(mode)}
          className="cfm-toggle-btn"
          style={{
            background: active === mode ? T.navy : "transparent",
            color:      active === mode ? T.gold : "#64748b",
          }}
        >
          <Icon size={13} />
        </button>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════
   Main component
══════════════════════════════════════════ */
export default function CaseFileManager() {
  const { refNo } = useParams();
  const [caseData,       setCaseData]       = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [viewMode,       setViewMode]       = useState("list");
  const [previewDoc,     setPreviewDoc]     = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloading,    setDownloading]    = useState(null);

  useEffect(() => {
    if (!refNo) return;
    fetch(`${API_BASE_URL}/api/case-documents/${encodeURIComponent(refNo)}`)
      .then((r) => {
        if (!r.ok) throw new Error(r.status === 404 ? "Case not found" : "Failed to load");
        return r.json();
      })
      .then((d) => { setCaseData(d); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, [refNo]);

  const openPreview = useCallback(async (doc) => {
    setPreviewLoading(true);
    setPreviewDoc({ label: doc.label, url: null });
    try {
      const blob = await fetchDocBlob(doc.serviceId);
      setPreviewDoc({ label: doc.label, url: URL.createObjectURL(blob) });
    } catch {
      setPreviewDoc(null);
      alert("Could not load this document for preview.");
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const closePreview = useCallback(() => {
    if (previewDoc?.url) URL.revokeObjectURL(previewDoc.url);
    setPreviewDoc(null);
  }, [previewDoc]);

  const handleDownload = useCallback(async (doc) => {
    setDownloading(doc.serviceId);
    try {
      const blob = await fetchDocBlob(doc.serviceId);
      triggerBlobDownload(blob, `${doc.label.replace(/\s+/g, "_")}_${refNo}.pdf`);
    } catch {
      alert("Failed to download document.");
    } finally {
      setDownloading(null);
    }
  }, [refNo]);

  if (loading) return (
    <PageCenter>
      <div style={{ textAlign: "center" }}>
        <div className="spinner-border" style={{ width: "2.5rem", height: "2.5rem", color: T.navy }} />
        <p style={{ marginTop: "1rem", color: "#555", fontWeight: 500 }}>Loading…</p>
      </div>
    </PageCenter>
  );

  if (error) return (
    <PageCenter>
      <div style={{ textAlign: "center" }}>
        <FaFolderOpen size={56} color={T.navy} style={{ opacity: 0.25, marginBottom: "1rem" }} />
        <h5 style={{ color: "#dc3545", fontWeight: 700 }}>{error}</h5>
        <p style={{ color: "#888" }}>Reference: <strong>{refNo}</strong></p>
      </div>
    </PageCenter>
  );

  const statusStyle = STATUS_CONFIG[caseData.status] ?? STATUS_CONFIG._default;
  const docs        = caseData.documents;

  const infoFields = [
    { label: "Reference No",  value: caseData.refNo,           nowrap: true },
    { label: "Customer Name", value: caseData.customerName || "—" },
    { label: "Account No",    value: caseData.accountNo    || "—" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: T.pageBg, fontFamily: T.font, display: "flex", flexDirection: "column" }}>
      <style>{STYLES}</style>

      {/* ── Topbar ── */}
      <div style={{ background: T.navy, boxShadow: "0 2px 8px rgba(0,0,0,0.3)", position: "sticky", top: 0, zIndex: 100 }}>
        <div className="container" style={{ padding: "0.85rem 1rem", display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: T.gold, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <FaFilePdf size={18} color={T.navy} />
          </div>
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: "1rem", lineHeight: 1.2 }}>Case Document Repository</div>
            <div style={{ color: T.gold, fontSize: "0.7rem", fontWeight: 500 }}>Resolution Experts Alliance Association — Arbitration File Manager</div>
          </div>
        </div>
      </div>

      {/* ── Case info card ── */}
      <div className="container" style={{ paddingTop: "1.5rem", paddingBottom: 0 }}>
        <div style={{ background: "#fff", borderRadius: 10, boxShadow: T.shadowSm, border: `1px solid ${T.border}`, borderLeft: `5px solid ${T.navy}`, padding: "1.1rem 1.4rem" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "2rem 3rem", alignItems: "flex-start" }}>
            {infoFields.map(({ label, value, nowrap }) => (
              <div key={label}>
                <div style={LABEL_STYLE}>{label}</div>
                <div style={{ fontWeight: 600, fontSize: "0.88rem", color: T.navy, whiteSpace: nowrap ? "nowrap" : undefined }}>{value}</div>
              </div>
            ))}
            <div>
              <div style={LABEL_STYLE}>Status</div>
              <span style={{
                display: "inline-block", padding: "2px 12px", borderRadius: 20, marginTop: 2,
                background: statusStyle.bg, color: statusStyle.color,
                border: `1px solid ${statusStyle.border}`,
                fontWeight: 600, fontSize: "0.75rem",
              }}>
                {caseData.status}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Documents section ── */}
      <div className="container" style={{ paddingTop: "1.25rem", paddingBottom: "2.5rem", flex: 1 }}>

        {/* Toolbar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem", paddingLeft: 2 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span style={{ fontWeight: 700, fontSize: "0.95rem", color: T.navy }}>Documents</span>
            <span style={{ background: T.border, color: "#64748b", fontSize: "0.7rem", fontWeight: 600, borderRadius: 20, padding: "1px 9px" }}>
              {docs.length}
            </span>
          </div>
          <ViewToggle active={viewMode} onChange={setViewMode} />
        </div>

        {/* Empty state */}
        {docs.length === 0 && (
          <div style={{ background: "#fff", borderRadius: 10, border: `1px solid ${T.border}`, boxShadow: T.shadowSm, padding: "3rem", textAlign: "center" }}>
            <FaFolderOpen size={48} color={T.navy} style={{ opacity: 0.2, marginBottom: "0.75rem" }} />
            <p style={{ color: "#aaa", margin: 0, fontWeight: 500 }}>No documents available yet.</p>
          </div>
        )}

        {/* List view */}
        {docs.length > 0 && viewMode === "list" && (
          <div style={{ background: "#fff", borderRadius: 10, border: `1px solid ${T.border}`, boxShadow: T.shadowSm, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", padding: "0.5rem 1.25rem", background: "#f8fafc", borderBottom: `1px solid ${T.border}` }}>
              {["Name", "Actions"].map((h) => (
                <span key={h} style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.muted }}>{h}</span>
              ))}
            </div>
            {docs.map((doc, idx) => (
              <DocListItem
                key={doc.serviceId}
                doc={doc}
                isLast={idx === docs.length - 1}
                onPreview={openPreview}
                onDownload={handleDownload}
                previewLoading={previewLoading}
                downloading={downloading}
              />
            ))}
          </div>
        )}

        {/* Grid view */}
        {docs.length > 0 && viewMode === "grid" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "1rem" }}>
            {docs.map((doc) => (
              <DocGridCard
                key={doc.serviceId}
                doc={doc}
                onPreview={openPreview}
                onDownload={handleDownload}
                downloading={downloading}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <div style={{ background: T.navy, borderTop: `2px solid ${T.gold}`, textAlign: "center", padding: "0.75rem", fontSize: "0.7rem", color: "rgba(255,255,255,0.4)" }}>
        Resolution Experts Alliance Association &nbsp;|&nbsp; Secure Document Repository &nbsp;|&nbsp; Documents are confidential and intended for authorised recipients only.
      </div>

      {/* ── Preview modal ── */}
      {previewDoc && (
        <PreviewModal doc={previewDoc} loading={previewLoading} onClose={closePreview} />
      )}
    </div>
  );
}
